const STORAGE_KEY = 'omenira:wishlist:v1';

const safeParse = (value) => {
  try {
    const parsed = JSON.parse(value ?? '[]');
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
};

const getWishlist = () => safeParse(window.localStorage.getItem(STORAGE_KEY));

const setWishlist = (handles) => {
  const uniqueHandles = [...new Set(handles.filter(Boolean))];
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(uniqueHandles));
  document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: uniqueHandles }));
};

const toggleHandle = (handle) => {
  const wishlist = getWishlist();
  if (wishlist.includes(handle)) {
    setWishlist(wishlist.filter((entry) => entry !== handle));
    return false;
  }

  wishlist.push(handle);
  setWishlist(wishlist);
  return true;
};

const getCurrency = () =>
  window.Shopify?.currency?.active ||
  document.querySelector('meta[property="og:price:currency"]')?.content ||
  'USD';

const formatMoney = (cents) =>
  new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency: getCurrency(),
  }).format((cents || 0) / 100);

const updateButtons = () => {
  const wishlist = getWishlist();
  document.querySelectorAll('[data-wishlist-button]').forEach((button) => {
    const handle = button.dataset.productHandle;
    const isSaved = wishlist.includes(handle);
    button.classList.toggle('is-active', isSaved);
    button.setAttribute('aria-pressed', String(isSaved));
  });
};

const updateCount = () => {
  const count = getWishlist().length;
  document.querySelectorAll('[data-wishlist-count]').forEach((element) => {
    element.textContent = String(count);
    element.hidden = count === 0;
  });
  document.querySelectorAll('[data-wishlist-open]').forEach((element) => {
    element.classList.toggle('has-items', count > 0);
    element.setAttribute('aria-label', count > 0 ? `Open wishlist, ${count} saved item${count === 1 ? '' : 's'}` : 'Open wishlist');
  });
};

const getFirstAvailableVariantId = (product) => {
  const available = product.variants?.find((v) => v.available);
  return (available || product.variants?.[0])?.id;
};

const addToCart = async (variantId, button) => {
  if (!variantId) return;
  const original = button.textContent;
  button.disabled = true;
  button.textContent = 'Adding…';
  try {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
    });
    if (!response.ok) throw new Error('Failed to add');
    button.textContent = 'Added!';
    document.dispatchEvent(new CustomEvent('cart:item-added'));
    setTimeout(() => {
      button.textContent = original;
      button.disabled = false;
    }, 1800);
  } catch {
    button.textContent = original;
    button.disabled = false;
  }
};

const itemTemplate = (product) => {
  const variantId = getFirstAvailableVariantId(product);
  const addBtnAttrs = variantId
    ? `data-wishlist-add-to-cart data-variant-id="${variantId}"`
    : 'disabled title="Out of stock"';
  return `
  <article class="wishlist-drawer__item">
    <a href="${product.url}" class="wishlist-drawer__item-media">
      ${
        product.featured_image
          ? `<img src="${product.featured_image}" alt="${product.title}" loading="lazy">`
          : '<div class="wishlist-drawer__item-placeholder"></div>'
      }
    </a>
    <div class="wishlist-drawer__item-content">
      <a href="${product.url}" class="wishlist-drawer__item-title">${product.title}</a>
      <p class="wishlist-drawer__item-price">${formatMoney(product.price)}</p>
      <div class="wishlist-drawer__item-actions">
        <button type="button" class="button wishlist-drawer__add-to-cart" ${addBtnAttrs}>Add to cart</button>
        <button type="button" class="button button-unstyled wishlist-drawer__remove" data-wishlist-remove="${product.handle}">
          Remove
        </button>
      </div>
    </div>
  </article>
`;
};

const loadProduct = async (handle) => {
  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${root}products/${handle}.js`);
  if (!response.ok) throw new Error(`Failed to load ${handle}`);
  return response.json();
};

const renderDrawer = async () => {
  const drawer = document.querySelector('[data-wishlist-drawer]');
  if (!drawer) return;

  const list = drawer.querySelector('[data-wishlist-items]');
  const emptyState = drawer.querySelector('[data-wishlist-empty]');
  if (!list || !emptyState) return;

  const addAllBtn = drawer.querySelector('[data-wishlist-add-all]');
  const handles = getWishlist();

  if (handles.length === 0) {
    list.innerHTML = '';
    emptyState.hidden = false;
    if (addAllBtn) addAllBtn.hidden = true;
    return;
  }

  emptyState.hidden = true;
  list.innerHTML = '<p class="wishlist-drawer__loading">Loading saved items...</p>';

  const products = await Promise.all(
    handles.map(async (handle) => {
      try {
        return await loadProduct(handle);
      } catch {
        return null;
      }
    })
  );

  const validProducts = products.filter(Boolean);
  list.innerHTML = validProducts.map(itemTemplate).join('');

  if (addAllBtn) addAllBtn.hidden = validProducts.length === 0;

  const validHandles = validProducts.map((product) => product.handle);
  if (validHandles.length !== handles.length) {
    setWishlist(validHandles);
  }
};

document.addEventListener('click', (event) => {
  const toggleButton = event.target.closest('[data-wishlist-button]');
  if (toggleButton) {
    event.preventDefault();
    toggleHandle(toggleButton.dataset.productHandle);
    return;
  }

  const removeButton = event.target.closest('[data-wishlist-remove]');
  if (removeButton) {
    event.preventDefault();
    const handle = removeButton.dataset.wishlistRemove;
    setWishlist(getWishlist().filter((entry) => entry !== handle));
    renderDrawer();
    return;
  }

  const addToCartBtn = event.target.closest('[data-wishlist-add-to-cart]');
  if (addToCartBtn) {
    event.preventDefault();
    addToCart(addToCartBtn.dataset.variantId, addToCartBtn);
    return;
  }

  const addAllBtn = event.target.closest('[data-wishlist-add-all]');
  if (addAllBtn) {
    event.preventDefault();
    const drawer = document.querySelector('[data-wishlist-drawer]');
    if (!drawer) return;
    drawer
      .querySelectorAll('[data-wishlist-add-to-cart]:not([disabled])')
      .forEach((btn) => addToCart(btn.dataset.variantId, btn));
    return;
  }
});

document.addEventListener('click', (event) => {
  if (event.target.closest('[data-wishlist-open]')) {
    renderDrawer();
  }
});

document.addEventListener('wishlist:updated', () => {
  updateButtons();
  updateCount();
});

window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY) {
    updateButtons();
    updateCount();
    renderDrawer();
  }
});

document.addEventListener('DOMContentLoaded', () => {
  updateButtons();
  updateCount();
});
